import * as React from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import LineBreak from "./LineBreak";

interface RevealBoxProps {
  image: string;
  title: string;
  subtitle: string;
  description: {
    start: string;
    ammo: {
      powder: string[];
      bullet: string[];
    };
  };
}

export default function RevealBox({
  image,
  title,
  subtitle,
  description,
}: RevealBoxProps): React.JSX.Element {
  const { i18n } = useTranslation();
  const [open, setOpen] = React.useState(false);

  return (
    <Box
      sx={{
        cursor: "pointer",
        borderRadius: 3,
        overflow: "hidden",
        position: "relative",
        width: "100%",
      }}
      onClick={() => setOpen((prev) => !prev)}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      {/* Image container */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          borderTopLeftRadius: 12,
          borderTopRightRadius: 12,
        }}
      >
        <Box
          component="img"
          src={image}
          alt={title}
          sx={{ width: "100%", height: "100%", objectFit: "cover" }}
        />

        {/* Bottom-up overlay */}
        <Box
          className="overlay"
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "100%",
            bgcolor: "rgba(0,0,0,0.85)",
            color: "#fff",
            p: 2,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            gap: 2,
            overflow: "auto",
            transform: open ? "translateY(0)" : "translateY(100%)",
            transition: "transform 0.35s ease",
          }}
        >
          <Typography variant="body2" sx={{ lineHeight: 1.2 }}>
            {description.start}
          </Typography>
          <Box>
            <Typography variant="body1" fontWeight={600}>
              {i18n.language === "en" ? "Powder:" : "パウダー："}
            </Typography>
            {description.ammo.powder.map((item) => (
              <Typography key={item} variant="body1" sx={{ display: "block" }}>
                • {item}
              </Typography>
            ))}
          </Box>
          <Box>
            <Typography variant="body1" fontWeight={600}>
              {i18n.language === "en" ? "Bullet:" : "弾頭："}
            </Typography>
            {description.ammo.bullet.map((item) => (
              <Typography key={item} variant="body1" sx={{ display: "block" }}>
                • {item}
              </Typography>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Title below the image */}
      <Box sx={{ bgcolor: "#fff", py: 1.5, px: 2, textAlign: "center" }}>
        <Typography
          variant="overline"
          fontWeight={600}
          fontSize="1.05rem"
          sx={{ color: "#000", lineHeight: 1.2 }}
        >
          {title}
        </Typography>
        <LineBreak />
        <Typography variant="caption" sx={{ color: "#000", lineHeight: 1.2 }}>
          {subtitle}
        </Typography>
      </Box>
    </Box>
  );
}
